import { Fragment, useState } from 'react';

import { COLUMNS_MARKET, useMarketsContext } from './MarketContext';

import { AssetInfoElement } from './AssetInfoElement';
import { CustomNumber } from '../CustomNumber/CustomNumber';
import { DropdownElement } from './DropdownElement';
import { ProfitLoss } from '../ProfitLoss';
import { columnClassName } from './MarketList';

import {
  ColumnsValues,
  InfoDataBond,
  InfoDataCRYPTO,
  InfoDataEtf,
  InfoDataFund,
  InfoDataSTOCK,
  MarketColumn,
  RowParams,
} from './types';
import { computePercentage } from '../../helpers/functions';
import moment from 'moment';

const currency = 'usd';

export const MarketListRow = ({ asset, gridColClassName, rank }: RowParams) => {
  const { assetType } = useMarketsContext();
  const [truncate, setTruncate] = useState(true);

  const { country, info: assetInfo, history, marketCap } = asset;
  const columns = COLUMNS_MARKET[assetType];
  const info = JSON.parse(assetInfo?.data ?? '{}') as InfoDataCRYPTO &
    InfoDataSTOCK &
    InfoDataBond &
    InfoDataFund &
    InfoDataEtf;

  const values: ColumnsValues = {
    ASSET: {},
    PRICE: {
      value: history?.[0]?.[currency],
      suffix: '$',
    },
    'VAR 24H': {
      value:
        info.marketData?.priceChangePercentage24hInCurrency?.[currency] ??
        computePercentage(
          history?.[1]?.[currency] ?? 0,
          history?.[0]?.[currency] ?? 0,
          100
        ),
      suffix: '%',
    },
    'VAR 7D': {
      value:
        info.marketData?.priceChangePercentage7dInCurrency?.[currency] ??
        computePercentage(
          history?.[6]?.[currency] ?? 0,
          history?.[0]?.[currency] ?? 0,
          100
        ),
      suffix: '%',
    },
    'MARKET CAP': {
      value: marketCap,
      suffix: '$',
    },
    ECOSYSTEM: {
      value: Object.values(info.categories ?? { a: '-' }).join(', '),
    },
    INDUSTRY: { value: info.industry },
    COUPON: { value: info.coupon ? +info.coupon : undefined },
    'MATURITY DATE': { value: info.maturityDate },
    RATING: { value: info.rating?.spRating },
    COUNTRY: { value: assetType === 'FOREX' ? country : undefined },
    AVG30D: {
      value: history?.length
        ? history.slice(0, 29).reduce((a, b) => a + b[currency], 0) /
          history.slice(0, 29).length
        : 0,
    },
    YTH: {
      value: history?.length
        ? Math.max(
            ...history
              .slice(
                0,
                history?.findIndex(
                  ({ date }) => moment(date).format('MM-DD') === '01-01'
                )
              )
              .map(({ [currency]: val }) => val)
          )
        : 0,
    },
    CATEGORY: { value: info.general?.category },
    COMPANY: { value: info.etfData?.companyName },
    TYPE: {},
    STRIKE: {},
    'EXPIRATION DATE': {},
    VOLUME: {},
    'OPEN INT': {},
    NAV: {
      value: info.mutualFundData?.nav ? +info.mutualFundData.nav : undefined,
    },
    YIELD: {
      value:
        info.mutualFundData?.yield || info.etfData?.yield
          ? Number(info.mutualFundData?.yield ?? info.etfData?.yield)
          : undefined,
    },
    'YIELD 1 YEAR': {
      value: info.mutualFundData?.yield1YearYtd
        ? +info.mutualFundData.yield1YearYtd
        : undefined,
    },
    'YIELD 3 YEAR': {
      value: info.mutualFundData?.yield3YearYtd
        ? +info.mutualFundData.yield3YearYtd
        : undefined,
    },
    'YIELD 5 YEAR': {
      value: info.mutualFundData?.yield5YearYtd
        ? +info.mutualFundData.yield5YearYtd
        : undefined,
    },
  };

  // alert(JSON.stringify(values, null, 2));

  return (
    <div
      className={`
        flex
        w-full
        min-h-16
        border-b
        text-sm
        border-grey-10
      `}
    >
      {/* RANKING */}
      <CustomNumber
        className={`${columnClassName} w-16 h-auto justify-center`}
        decimalScale={0}
        displayType="text"
        value={rank}
      />

      <div className={`${gridColClassName}`}>
        {/* ASSET */}
        <AssetInfoElement asset={asset} logoSize={6} />

        {Object.entries(values).reduce(
          (arr, [key, { value, suffix }]) =>
            columns.slice(1).includes(key as MarketColumn)
              ? [
                  ...arr,
                  <Fragment key={key}>
                    {value === null ||
                    value === undefined ||
                    typeof value === 'string' ? (
                      <div
                        className={`${columnClassName} cursor-pointer`}
                        onClick={() => setTruncate((prev) => !prev)}
                      >
                        {(value ?? '-')
                          .slice(...(truncate ? [0, 30] : [0]))
                          .concat(
                            truncate && (value ?? '-')?.length >= 30
                              ? '...'
                              : ''
                          )}
                      </div>
                    ) : suffix === '%' ? (
                      <ProfitLoss
                        className={columnClassName}
                        displayType="text"
                        percentageClassName="text-sm"
                        percentage={value}
                      />
                    ) : (
                      <CustomNumber
                        className={columnClassName}
                        value={value}
                        displayType="text"
                        suffix={suffix}
                      />
                    )}
                  </Fragment>,
                ]
              : arr,
          [] as JSX.Element[]
        )}
      </div>

      {/* OPTIONS */}
      <DropdownElement asset={asset} />
    </div>
  );
};
